import React, { useMemo, useState, useEffect } from "react";
import { useParams } from "react-router-dom";

const LogoMark = () => (
  <div className="flex items-center gap-2">
    <div className="flex h-9 w-9 items-center justify-center rounded-md bg-[#14b85a] text-white text-base font-bold">
      B
    </div>
    <span className="text-[20px] font-extrabold text-[#14b85a]">BUY iT</span>
  </div>
);

const TopBar = () => (
  <div className="flex items-center justify-between border-b border-gray-200 bg-[#f3f4f4] px-6 py-4">
    <LogoMark />
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#14b85a] text-sm font-semibold text-white">
        J
      </div>
      <div className="text-right">
        <p className="text-sm font-semibold text-[#0b0b0b]">Good Afternoon, Jeff</p>
        <p className="text-xs text-gray-600">Order validator</p>
      </div>
    </div>
  </div>
);

const Section = ({ title, children }) => (
  <section className="rounded-lg bg-white p-5 shadow-sm">
    <h2 className="text-base font-semibold text-[#0b0b0b]">{title}</h2>
    <div className="mt-4">{children}</div>
  </section>
);

const DetailItem = ({ label, value }) => (
  <div className="flex flex-col gap-1 rounded-md border border-gray-100 bg-[#f9faf9] px-3 py-2">
    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
    <p className="text-sm font-semibold text-[#0b0b0b]">{value}</p>
  </div>
);

const DetailsGrid = ({ items }) => (
  <div className="grid grid-cols-2 gap-3">
    {items.map((item) => (
      <DetailItem key={item.label} label={item.label} value={item.value} />
    ))}
  </div>
);

const ProductImageCard = ({ src }) => (
  <div className="h-36 w-44 overflow-hidden rounded-lg bg-white shadow-sm">
    <img loading="lazy" src={src} alt="Product" className="h-full w-full object-cover" />
  </div>
);

const InvalidateModal = ({ open, reason, setReason, onClose, onSubmit }) => {
  if (!open) return null;
  const canSubmit = reason.trim().length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="absolute inset-0" onClick={onClose} aria-hidden="true" />
      <div className="relative z-10 w-full max-w-md rounded-xl bg-white p-6 shadow-lg">
        <h3 className="text-lg font-semibold text-[#0b0b0b]">Invalidate Order</h3>
        <p className="mt-2 text-sm text-gray-600">Please provide a reason for invalidation.</p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          className="mt-4 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-[#0b0b0b] focus:border-[#14b85a] focus:outline-none"
          placeholder="Type reason here..."
        />
        <div className="mt-5 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
            className={`rounded-lg px-4 py-2 text-sm font-semibold text-white ${
              canSubmit ? "bg-red-600 hover:bg-red-700" : "cursor-not-allowed bg-gray-400"
            }`}
          >
            Invalidate
          </button>
        </div>
      </div>
    </div>
  );
};

const ConfirmOrderScreen = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState(null);
  const { orderItemId } = useParams();

  useEffect(() => {
    if (!orderItemId) return;
    const fetchOrder = async () => {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("adminToken");
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/orders/item/${orderItemId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        if (!data.success) {
          throw new Error(data.message || "Failed to load order");
        }
        setOrderData(data.data || null);
      } catch (err) {
        setError(err.message);
        setOrderData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderItemId]);

  const images = useMemo(() => {
    const fallback = [
      "https://images.unsplash.com/photo-1512436991641-6745cdb1723f?w=600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1514996937319-344454492b37?w=600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=600&auto=format&fit=crop",
      "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=600&auto=format&fit=crop",
    ];
    if (!orderData?.product?.productImages?.length) return fallback;
    return orderData.product.productImages;
  }, [orderData]);

  const productDetails = useMemo(() => {
    const product = orderData?.product;
    if (!product) {
      return [
        { label: "Vendor Store", value: "Debz Cosmetics Store" },
        { label: "Product Name", value: "HP 17 Series Laptop" },
        { label: "Product Description", value: "Large-format laptop for productivity." },
        { label: "Size", value: "Big" },
        { label: "Category", value: "Electronics & Gadgets" },
        { label: "Color", value: "Black" },
        { label: "Weight", value: "3kg" },
        { label: "Age Group", value: "Adults" },
        { label: "Selling Price", value: "NGN 120,000" },
        { label: "Note", value: "Handle with care" },
        { label: "Gender", value: "Unisex" },
      ];
    }
    return [
      { label: "Vendor Store", value: product.vendorName || "N/A" },
      { label: "Product Name", value: product.productName || "N/A" },
      { label: "Product Description", value: product.description || "N/A" },
      { label: "Size", value: product.productSize || "N/A" },
      { label: "Category", value: product.category || "N/A" },
      { label: "Color", value: product.productColor || "N/A" },
      { label: "Weight", value: product.productWeight || "N/A" },
      { label: "Age Group", value: product.ageGroup || "N/A" },
      { label: "Selling Price", value: `NGN ${Number(product.price || 0).toLocaleString("en-NG")}` },
      { label: "Note", value: product.productNote || "N/A" },
      { label: "Gender", value: product.gender || "N/A" },
    ];
  }, [orderData]);

  const orderDetails = useMemo(() => {
    const product = orderData?.product;
    if (!orderData) {
      return [
        { label: "Product Name", value: "HP 17 Series Laptop" },
        { label: "Product Description", value: "Large-format laptop for productivity." },
        { label: "Buyer Name", value: "David Son of God" },
        { label: "Buyer Phone Number", value: "07078145852" },
        { label: "Order ID", value: "E7E287" },
        { label: "Order Quantity", value: "1" },
        { label: "Buyer Email", value: "nosad@gmail.com" },
        { label: "Buyer Address", value: "14 Grace Abosede, Abraka" },
        { label: "Postal Code", value: "490135" },
      ];
    }
    return [
      { label: "Product Name", value: product?.productName || "N/A" },
      { label: "Product Description", value: product?.description || "N/A" },
      { label: "Buyer Name", value: orderData.buyerFullName || "N/A" },
      { label: "Buyer Phone Number", value: orderData.buyerPhone || "N/A" },
      { label: "Order ID", value: orderData.orderNumber || "N/A" },
      { label: "Order Quantity", value: product?.quantity ?? "N/A" },
      { label: "Buyer Email", value: orderData.buyerEmail || "N/A" },
      { label: "Buyer Address", value: orderData.buyerAddress || "N/A" },
      { label: "Postal Code", value: orderData.postalCode || "N/A" },
    ];
  }, [orderData]);

  const confirmOrder = () => {
    if (!orderItemId) return;
    const runConfirm = async () => {
      try {
        const token = localStorage.getItem("adminToken");
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/orders/confirm/${orderItemId}`, {
          method: "PATCH",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();
        if (!data.success) {
          throw new Error(data.message || "Failed to confirm order");
        }
        setOrderData((prev) =>
          prev
            ? {
                ...prev,
                productStatus: data.data?.productStatus || "Placed For Delivery",
              }
            : prev
        );
        setMessage(data.message || "Product confirmed successfully.");
        setSuccess(true);
        setTimeout(() => setMessage(""), 2000);
      } catch (err) {
        setMessage(err.message || "Failed to confirm order");
        setSuccess(false);
        setTimeout(() => setMessage(""), 2000);
      }
    };
    runConfirm();
  };

  const submitInvalidate = () => {
    if (!orderItemId || !reason.trim()) return;
    const runReject = async () => {
      try {
        const token = localStorage.getItem("adminToken");
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/orders/reject/${orderItemId}`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ reason: reason.trim() }),
        });
        const data = await res.json();
        if (!data.success) {
          throw new Error(data.message || "Failed to reject order");
        }
        setOrderData((prev) =>
          prev
            ? {
                ...prev,
                productStatus: data.data?.productStatus || prev.productStatus,
              }
            : prev
        );
        setMessage(data.message || "Product rejected successfully.");
        setSuccess(true);
        setTimeout(() => setMessage(""), 2000);
        setIsModalOpen(false);
        setReason("");
      } catch (err) {
        setMessage(err.message || "Failed to reject order");
        setSuccess(false);
        setTimeout(() => setMessage(""), 2000);
      }
    };
    runReject();
  };

  return (
    <div className="min-h-screen w-full overflow-x-auto bg-[#f3f4f4] text-[#0b0b0b]">
      {message && (
        <div
          className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 px-6 py-3 rounded-lg shadow-lg text-sm font-semibold animate-fadeInOut ${
            success ? "bg-[#19C463] text-white" : "bg-red-500 text-white"
          }`}
          style={{ minWidth: "220px", textAlign: "center", letterSpacing: "0.5px" }}
        >
          {message}
        </div>
      )}
      <div className="min-h-screen min-w-[600px]">
        <TopBar />
        <main className="px-6 py-6">
          <div className="mb-4">
            <h1 className="text-xl font-semibold">Confirm Order</h1>
            <p className="mt-1 text-sm text-gray-600">Review details before confirming the order.</p>
          </div>

          {loading ? (
            <div className="rounded-lg bg-white p-6 text-center text-sm text-gray-600 shadow-sm">
              Loading order details...
            </div>
          ) : error ? (
            <div className="rounded-lg bg-white p-6 text-center text-sm text-red-600 shadow-sm">
              {error}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-4 gap-4">
                {images.map((src, index) => (
                  <ProductImageCard key={`${src}-${index}`} src={src} />
                ))}
              </div>

              <div className="mt-6 grid grid-cols-2 gap-6">
                <Section title="Product Details">
                  <DetailsGrid items={productDetails} />
                </Section>
                <Section title="Order Details">
                  <DetailsGrid items={orderDetails} />
                </Section>
              </div>
            </>
          )}

          <div className="mt-6 flex items-center gap-4">
            <button
              type="button"
              onClick={confirmOrder}
              className="rounded-lg bg-[#14b85a] px-5 py-2 text-sm font-semibold text-white hover:bg-[#119e4d]"
            >
              Confirm order
            </button>
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="rounded-lg border border-gray-300 px-5 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              Invalidate
            </button>
          </div>
        </main>
      </div>

      <InvalidateModal
        open={isModalOpen}
        reason={reason}
        setReason={setReason}
        onClose={() => setIsModalOpen(false)}
        onSubmit={submitInvalidate}
      />
    </div>
  );
};

export default ConfirmOrderScreen;
